import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import { t } from "i18next";
import { AttributeTabsCustom  } from "./AttributeTabs.style";
import { TabsSkeleton } from "./TabsSkeleton.component";
import { FILTERS } from "../../consts";
import { useLearnerProgressQuery } from "../../queries";
import { useAuthStore } from "../../stores";
import { useAttributeTabStore } from "../../stores/attributeTabStore";


type IFilterTabs = {
  children?: React.ReactNode;
  onClickHandler: (value: string) => void;
}

export type Attribute = {
  attributeId: number|string
  corporateId: number
  attribute: string
  userDeleted: boolean
  creationDate: string
  updateDate: string
  forYou: boolean
  exploreCarousel: boolean
  deleted: boolean
}

type NewAttribute = {
    attributeName: string,
    attributeId: number|string,
    los: number,
    completedLos: number,
};
export const AttributeTabs = ({ onClickHandler, children }: IFilterTabs) => {
  const attributes = useAuthStore(state => state.userAttributes)||[];
  const { setSelectedTab, selectedTab, resetAttribute } = useAttributeTabStore();
  const { isFetching } = useLearnerProgressQuery();

  const filterAttributes = attributes.filter(attr => attr.completedLos !== attr.los && attr.los > 0);

  // Trova il primo elemento di attributes che è presente anche in filterAttributes
  const firstAttribute = attributes.length > 0 ? filterAttributes.find(attr => attr.attributeId === attributes[0].attributeId) : null;

  // Ottieni gli altri attributi filtrati (escluso il primo se presente)
  const otherFilteredAttributes = firstAttribute
    ? filterAttributes.filter(attr => attr.attributeId !== firstAttribute.attributeId)
    : filterAttributes;

  const attributesWithDefault:NewAttribute[] = [
    {
      attributeId: "total",
      attributeName: "Tutti",
      los: 0,
      completedLos: 0
    },
    ...(firstAttribute ? [firstAttribute] : []),
    {
      attributeId: "mandatory",
      attributeName: t("MANDATORY_ATTRIBUTE"),
      los: 0,
      completedLos: 0
    },
    ...otherFilteredAttributes
  ].filter((item, index) => {
    // Se non c'è il primo attributo, rimuovi "mandatory" dalla posizione 2 e mettilo al secondo posto
    if (!firstAttribute && item.attributeId === "mandatory" && index === 2) {
      return false;
    }
    return true;
  });

  // Se non c'è il primo attributo, riorganizza per mettere mandatory al secondo posto
  if (!firstAttribute) {
    const totalTab = attributesWithDefault.find(item => item.attributeId === "total");
    const mandatoryTab = {
      attributeId: "mandatory",
      attributeName: t("MANDATORY_ATTRIBUTE"),
      los: 0,
      completedLos: 0
    };

    // Ricostruisci l'array con l'ordine corretto
    const newOrder = [];
    if (totalTab) newOrder.push(totalTab);
    newOrder.push(mandatoryTab);
    newOrder.push(...otherFilteredAttributes);

    attributesWithDefault.length = 0;
    attributesWithDefault.push(...newOrder);
  }

  const theme = useTheme();

  const handleChange = (filter: NewAttribute) => {
    const value = filter.attributeId.toString();
    const widgetFilter = filter.attributeId.toString();
    onClickHandler(value);

    if (value === FILTERS.ALL) {
      resetAttribute();
      return;
    }
    setSelectedTab(widgetFilter);

  };

  const a11yProps = (index: string) => {
    return {
      id: `filter-tab-${index}`,
      "aria-controls": `filter-tabpanel-${index}`
    };
  };

  if(isFetching && attributes.length === 0) return <TabsSkeleton />;
  if(attributes.length === 0) return <div style={ { marginTop:"0.5rem" } }>{ children }</div>;

  return (
    <>
      <AttributeTabsCustom
        sx={ {
          ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
          ".Mui-selected": { color: theme.customColors.accentVariantA },
          fontFamily: "Inter"
        } }
        TabIndicatorProps={ { style: { background: theme.customColors.accentVariantA, borderRadius: "1.5rem" } } }
        variant="scrollable"
        scrollButtons={ true }
        allowScrollButtonsMobile
        value={ selectedTab.toString() }
        aria-label="filter tabs for you page">
        { (attributesWithDefault||[]).length > 1 ? attributesWithDefault.map((filter: NewAttribute, index) => (
          <Tab
            sx={ {
              textTransform: "none",
              fontSize: "0.875rem",
              letterSpacing: "0.0313em",
              fontWeight: 600,
              fontStyle: "semibold",
              height: "1.5rem",
              minWidth: "3rem",
              marginX:"0.35rem",
              lineHeight: "1.5rem",
              borderBottom: `2px solid ${theme.customColors.border}`,
              "&.Mui-focusVisible": {
                border: `1px solid ${theme.customColors.accentVariantA}`
              }
            } }
            key={ index }
            label={ filter.attributeName }
            value={ filter.attributeId.toString() }
            onClick={ () => handleChange(filter) }
            { ...a11yProps(filter.attributeId.toString()) }
          />
        )):null }
      </AttributeTabsCustom>
      { (attributesWithDefault||[]).map((filter: NewAttribute, index:number) => (
        <div
          tabIndex={ -1 }
          key={ index }
          role="tabpanel"
          hidden={ filter.attributeId.toString() !== selectedTab }
          id={ `filter-tabpanel-${index}` }
          aria-labelledby={ `filter-tab-${filter.attributeId.toString()}` }
        >
          { selectedTab === filter.attributeId.toString() && <Box>{ children }</Box> }
        </div>
      ))
      }
    </>
  );
};
